import { Dispatch, SetStateAction } from 'react';

import { createContext } from '@lyearn/core/utils/context';

import { MicroSkillType } from '../types';

export type UIState = {
  selectedMicroSkillId?: string;
  selectedSkillIds?: string[];
  action?: 'ADD' | 'EDIT';
  canEdit?: boolean;
};

export type MicroSkillActions = {
  isMicroSkillSelected?: (microSkill: Pick<MicroSkillType, 'id'>) => boolean;
  toggleMicroSkillSelection?: (microSkill: Pick<MicroSkillType, 'id'>) => void;
  onAddMicroSkill?: (parentMicroSkill?: Pick<MicroSkillType, 'id' | 'childrenIds'>) => void;
  onEditMicroSkill?: (microSkill: MicroSkillType) => void;
  onRemoveMicroSkill?: (microSkill: Pick<MicroSkillType, 'id' | 'parentIds'>) => void;
  fetchMicroSkills?: (parentMicroSkill?: Pick<MicroSkillType, 'id'>) => void;
};

export type MicroSkillsContextType = {
  actions?: MicroSkillActions;
  uiState?: UIState;
  setUIState?: Dispatch<SetStateAction<UIState>>;
};

const [MicroSkillContextProvider, useMicroSkillContext] =
  createContext<MicroSkillsContextType>('MicroSkillsContext');

const useMicroSkillActions = (microSkill: Pick<MicroSkillType, 'id' | 'parentIds' | 'name'>) => {
  const { actions, uiState, setUIState } = useMicroSkillContext();
  const { id } = microSkill;

  const isMicroSkillEditableState =
    uiState?.selectedMicroSkillId === id &&
    (uiState?.action === 'ADD' || uiState?.action === 'EDIT');

  return {
    uiState: {
      ...uiState,
      isMicroSkillEditableState,
      selectedSkillIds: uiState?.selectedSkillIds ?? [],
    },
    actions: {
      onAddMicroSkill: () => actions?.onAddMicroSkill?.(microSkill),
      onRemoveMicroSkill: () => actions?.onRemoveMicroSkill?.(microSkill),
      onEditMicroSkill: ({ name }: { name: MicroSkillType['name'] }) =>
        actions?.onEditMicroSkill?.({ ...microSkill, name }),
      isMicroSkillSelected: () => actions?.isMicroSkillSelected?.(microSkill),
      toggleMicroSkillSelection: () => actions?.toggleMicroSkillSelection?.(microSkill),
      onSetMicroSkillEditState: () =>
        // to prevent input on calling blur on edit mode have to use setTimeout
        setTimeout(
          () => setUIState?.((prev) => ({ ...prev, selectedMicroSkillId: id, action: 'EDIT' })),
          100,
        ),
      onRemoveMicroSkillEditState: () =>
        // to prevent input on calling blur on edit mode have to use setTimeout
        setTimeout(
          () =>
            setUIState?.((prev) => ({
              ...prev,
              selectedMicroSkillId: undefined,
              action: undefined,
            })),
          100,
        ),
    },
  };
};

export { MicroSkillContextProvider, useMicroSkillActions };
